import moment from 'moment';
import { CompositeModel } from '../../models/complex/composite.model';
import { ArrayModel } from '../../models/complex/array.model';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { NotificationIdentifier } from '../../types/notification-identifier';
import { DateModel } from '../../models/primitive/date.model';
import { NumberModel } from '../../models/primitive/number.model';
import { RadioModel } from '../../models/complex/radio.model';
import { TextModel } from '../../models/primitive/text.model';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ConfigConstants } from '../../config/config-constants';
import { CustomInjector } from '../custom-injector/custom-injector';
import { TranslateService } from '@ngx-translate/core';
import { DateUtilities } from '../../utilities/date-utilities';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NumberModelOptions } from '../../models/options/number-model-options';
import { isDefined } from '../../utilities/is-defined';
import { DurationModel } from '../../models/primitive/duration.model';
import { DurationModelOptions } from '../../models/options/duration-model-options';
import { DateModelOptions } from '../../models/options/date-model-options';

export enum APPOINTMENT_SLOT_STATUS {
    AVAILABLE,
    NOT_AVAILABLE,
}

export class AppointmentPeriodModel extends CompositeModel {
    private readonly timeFormat: string;
    public status: APPOINTMENT_SLOT_STATUS;
    public isLoading = false;
    public disabled = false;
    public constructor(
        public startDateAndTime: string,
        public finishDateAndTime: string,
    ) {
        super('AppointmentPeriodModel');

        const injector = CustomInjector.getInjector();
        this.timeFormat = injector.get(ClientManagerInstanceToken).getTimeFormat();
    }

    public getFormattedDate(): string {
        return DateUtilities.formatDate(moment.utc(this.startDateAndTime).local());
    }

    public getFormattedTime(): string {
        return moment.utc(this.startDateAndTime).local().format(this.timeFormat);
    }
}

export class AppointmentSlotsModel {
    public slotIntervals: ArrayModel<AppointmentPeriodModel>;
    public duration: DurationModel;
    public days: NumberModel;
    public selectedSlotStartDate: DateModel;
    public selectedSlotFinishDate: DateModel;
    public dedicatedResource?: RadioModel<TextModel>;
    public isAppointmentBooked = false;
    public wotSysId: string;

    public constructor(
        public notificationIdentifier: NotificationIdentifier,
        public followUpShortText = '',
        public followUpLongText = '', // this will be used once SENSE ads this field from their end. Please do not delete it
        duration: number,
        appBookingConfigs: AppointmentBookingConfigs,
        isDedicatedResourceEnabled: boolean,
        isDedicatedResourceRequired: boolean,
    ) {
        const startOfToday = moment().startOf('day');

        this.duration = new DurationModel(
            'FollowUpVisitAddTime',
            moment.duration(duration, 'minutes'),
            new DurationModelOptions({
                step: appBookingConfigs.additionalTimeStep,
                unit: 'minutes',
                minValue: moment.duration(appBookingConfigs.minAdditionalTime, 'minutes'),
                maxValue: moment.duration(appBookingConfigs.maxDuration, 'hours'),
            }),
        );

        const configMinDate = startOfToday.clone().add(appBookingConfigs.minDate, 'days');
        const configMaxDate = startOfToday.clone().add(appBookingConfigs.maxDate, 'days');
        this.selectedSlotStartDate = new DateModel('AppointmentStartDate', configMinDate.clone());
        this.selectedSlotFinishDate = new DateModel(
            'AppointmentEndDate',
            configMaxDate.clone(),
            new DateModelOptions({ readonly: true }),
        );
        this.selectedSlotStartDate.options = {
            ...this.selectedSlotStartDate.options,
            readonly: false,
            minValue: configMinDate.clone(),
            maxValue: this.selectedSlotFinishDate.value.clone(),
        };

        const difference = this.selectedSlotFinishDate.value.clone().diff(this.selectedSlotStartDate.value, 'days');
        const maxValue = this.selectedSlotFinishDate.options.maxValue
            .clone()
            .diff(this.selectedSlotStartDate.value, 'days');
        this.days = new NumberModel('days', difference, new NumberModelOptions({ minValue: 0, maxValue }));

        if (isDedicatedResourceEnabled) {
            this.initializeDedicatedResourceFields(isDedicatedResourceRequired);
        }
    }

    private initializeDedicatedResourceFields(required: boolean): void {
        const optionGroup: Array<DisplayDecorator<TextModel>> = [];
        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);

        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
            ),
        );
        this.dedicatedResource = new RadioModel<TextModel>('DedicatedResource', optionGroup);
        const selectedValue = required ? ConfigConstants.optionValueYes : ConfigConstants.optionValueNo;
        this.dedicatedResource.selectByPredicate((entry) => entry.value === selectedValue);
    }

    public updateLimitsForAppointmentStart(): void {
        if (
            this.selectedSlotStartDate.value.isAfter(this.selectedSlotFinishDate.options.maxValue) ||
            this.selectedSlotStartDate.value.isBefore(this.selectedSlotStartDate.options.minValue)
        ) {
            return;
        }
        if (this.selectedSlotStartDate.value.isAfter(this.selectedSlotFinishDate.value)) {
            this.selectedSlotFinishDate.value = this.selectedSlotStartDate.value.clone();
        }

        const difference = this.selectedSlotFinishDate.value.clone().diff(this.selectedSlotStartDate.value, 'days');
        const maxValue = this.selectedSlotFinishDate.options.maxValue
            .clone()
            .diff(this.selectedSlotStartDate.value, 'days');
        this.days.value = difference;
        this.days = assignModelOptions(this.days, { ...this.days.options, minValue: 0, maxValue });
    }

    public updateLimitsForDays(): void {
        if (!isDefined(this.days.value) || this.days.value < 0) {
            return;
        }
        const newFinishDate = this.selectedSlotStartDate.value.clone().add(this.days.value, 'days');

        if (newFinishDate.isSameOrBefore(this.selectedSlotFinishDate.options.maxValue)) {
            this.selectedSlotFinishDate.value = newFinishDate;
            this.selectedSlotFinishDate = assignModelOptions(
                this.selectedSlotFinishDate,
                this.selectedSlotFinishDate.options,
            );
        }
    }

    public getAppointmentGetPayload(): ODataGetAppointmentPayload {
        return {
            NotificationNumber: this.notificationIdentifier.qmNum,
            TaskNumber: parseInt(this.notificationIdentifier.maNum, 10),
            Start: this.selectedSlotStartDate.value.toISOString(),
            Finish: this.selectedSlotFinishDate.value.clone().endOf('day').toISOString(),
            DedicatedResource: this.dedicatedResource?.selectedEntry.value === ConfigConstants.optionValueYes,
            NumberOfSlotsToReturn: 30,
            Duration: this.duration.value?.asSeconds() ?? this.duration.options.minValue.asSeconds(),
            ShortText: this.followUpShortText,
            LongText: this.followUpLongText,
            UserName: this.notificationIdentifier.frontId,
        };
    }

    public updateAppointmentSlots(data: OdataAppointmentSlotResponseModel): void {
        this.wotSysId = data.WotSysId;
        this.slotIntervals = new ArrayModel<AppointmentPeriodModel>('Period');
        this.slotIntervals.addModels(
            data.SlotIntervals.filter((period) => {
                const minimumGrade = 80;
                return period.Grade >= minimumGrade;
            }).map((period) => new AppointmentPeriodModel(period.Start, period.Finish)),
        );
    }

    public updateLoadingStatus(index: number, isLoading: boolean): void {
        this.slotIntervals.value.forEach((slot, i) => {
            slot.disabled = isLoading;
            if (i === index) {
                slot.isLoading = isLoading;
            }
        });
    }

    public getAppointmentBookPayloadForIndex(index: number): OdataAppointmentBookPayload {
        const selectedSlot = this.slotIntervals.value[index];
        return {
            WotSysId: this.wotSysId,
            Start: selectedSlot.startDateAndTime,
            Finish: selectedSlot.finishDateAndTime,
        };
    }

    public updatedAppointmentStatus(index: number, status: boolean): void {
        this.slotIntervals.value[index].status = status
            ? APPOINTMENT_SLOT_STATUS.AVAILABLE
            : APPOINTMENT_SLOT_STATUS.NOT_AVAILABLE;
    }
}

export type ODataGetAppointmentPayload = {
    NotificationNumber: string;
    TaskNumber: number;
    Start: string;
    Finish: string;
    DedicatedResource: boolean;
    NumberOfSlotsToReturn: number;
    Duration: number;
    ShortText: string;
    LongText: string;
    UserName: string;
};

export interface AppointmentPeriod {
    Start: string;
    Finish: string;
    Grade: number;
}

export interface OdataAppointmentBookPayload extends ODataObject {
    WotSysId: string;
    Start: string;
    Finish: string;
}

export interface OdataAppointmentBookResponsePayload extends ODataObject {
    StatusCode: number;
}

export interface OdataAppointmentSlotResponseModel {
    WotSysId: string;
    SlotIntervals: AppointmentPeriod[];
}

export interface AppointmentBookingConfigs {
    minDate: number;
    maxDate: number;
    minAdditionalTime: number;
    maxDuration: number;
    additionalTimeStep: number;
}
